import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { useLanguage } from '../../context/LanguageContext';
import { Check, AlertTriangle, Move, BookOpen, Lock } from 'lucide-react';
import { TRADER_CONFIG, TRADER_COL_WIDTH } from './layoutUtils';

export const LaneNode = memo(({ data }) => {
  const { trader, width, height, imageLink } = data;
  const { t } = useLanguage();
  const config = TRADER_CONFIG[trader] || TRADER_CONFIG['Fence'];
  const normalizedName = Object.keys(TRADER_CONFIG).find(key => key === trader)?.toLowerCase() || trader.toLowerCase();

  return (
    <div className="relative pointer-events-none select-none flex h-full border-b-[4px] border-zinc-900/80" style={{ width: width, height: height }}>

      {/* İŞTE GERİ GELEN RENKLİ TÜCCAR AYIRACI */}
      <div className="absolute top-0 left-0 w-full h-[16px] opacity-70" style={{ backgroundColor: config?.color || '#333' }}></div>

      <div className="relative flex-shrink-0 h-full bg-[#050505] border-r-[16px] border-zinc-900" style={{ width: TRADER_COL_WIDTH }}>
        <div className="absolute inset-0 bg-cover bg-center grayscale opacity-100" style={{ backgroundImage: `url(${imageLink})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#050505]/30 to-[#050505]"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
        <div className="absolute bottom-24 left-24 flex items-center gap-12">
          <div className="w-16 h-full rounded-full" style={{ backgroundColor: config?.color || '#333' }}></div>
          <span className="text-[12rem] font-black text-white/20 uppercase tracking-widest leading-none">{t(`trader_${normalizedName}`) || trader}</span>
        </div>
      </div>
    </div>
  );
});

export const QuestNode = memo(({ data }) => {
  const { label, trader, level, completed, locked, wikiLink, isCompact, externalReqs, isActive } = data;
  const { t } = useLanguage();
  const normalizedName = trader.toLowerCase();

  const config = TRADER_CONFIG[trader] || TRADER_CONFIG['Fence'];

  let borderColor = locked ? 'border-zinc-800' : 'border-zinc-700';
  let bgColor = locked ? 'bg-[#09090b]' : 'bg-[#0c0c0e]';
  let textColor = locked ? 'text-zinc-600' : 'text-zinc-200';
  let shadowClass = 'shadow-2xl shadow-black/50';
  let scaleClass = 'hover:scale-[1.02]';
  let indicatorColor = 'bg-zinc-800';

  if (completed) {
    borderColor = 'border-emerald-900/50';
    bgColor = 'bg-emerald-950/20';
    textColor = 'text-emerald-600 line-through decoration-[8px]';
    indicatorColor = 'bg-emerald-600';
    shadowClass = 'shadow-none';
  } else if (isActive) {
    borderColor = 'border-amber-400'; // Yarı saydamlığı kaldırdık, saf parlak sarı
    bgColor = 'bg-gradient-to-br from-[#1a1500] to-[#0c0c0e]';
    shadowClass = 'shadow-[0_0_80px_rgba(251,191,36,0.25)] border-[12px]'; // Çerçeveyi 8px'den 12px'e çıkardık
    scaleClass = 'scale-[1.02] hover:scale-[1.04] z-50';
    textColor = 'text-amber-50 font-black';
    indicatorColor = 'bg-amber-400 shadow-[0_0_40px_rgba(251,191,36,0.8)] animate-pulse';
  }

  return (
    <div className={`relative rounded-3xl border-[8px] transition-all duration-300 group flex flex-col backdrop-blur-xl cursor-pointer select-none overflow-hidden ${isCompact ? 'w-[1200px] min-h-[500px] p-24' : 'w-[2000px] min-h-[800px] p-32'} ${bgColor} ${borderColor} ${shadowClass} ${scaleClass} hover:z-50`} onClick={(e) => { e.stopPropagation(); data.onToggle(); }}>

      <div className="absolute top-0 left-0 right-0 h-[8px] w-full" style={{ backgroundColor: completed ? '#10b981' : isActive ? '#f59e0b' : '#27272a' }}></div>

      <div
        className="custom-drag-handle absolute -top-12 -right-12 w-48 h-48 bg-[#050505] hover:bg-emerald-600 text-zinc-500 hover:text-white rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-all z-50 border-[8px] border-zinc-800 shadow-2xl hover:scale-110"
        onClick={(e) => e.stopPropagation()}
      >
        <Move size={90} strokeWidth={3} />
      </div>

      <Handle type="target" position={Position.Left} className="!bg-transparent !border-0 !w-32 !h-32 !-left-16" />

      {!isCompact && (
        <div className="flex justify-between items-center mb-24 opacity-80">
          <span className="text-7xl font-bold uppercase text-zinc-500 tracking-widest">{t(`trader_${normalizedName}`) || trader}</span>
          {level > 1 && (
            <span
              className="text-6xl font-black text-white px-16 py-8 rounded-2xl flex items-center gap-8 shadow-xl border-[4px] border-white/20"
              style={{ backgroundColor: config.color, boxShadow: `0 10px 40px -10px ${config.color}80` }}
            >
              LVL <span>{level}</span>
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-32 flex-1 h-full relative">
        <div className={`flex-shrink-0 rounded-full flex items-center justify-center transition-colors duration-300 ${isCompact ? 'w-48 h-48' : 'w-64 h-64'} ${completed ? 'bg-emerald-500/20 text-emerald-500' : isActive ? 'bg-amber-500/10 border-[8px] border-amber-500/50 text-amber-500' : 'bg-[#050505] border-[8px] border-zinc-800 text-zinc-700'}`}>
          {completed ? <Check className="w-32 h-32" strokeWidth={6} /> : locked ? <Lock className="w-24 h-24" strokeWidth={4} /> : <div className={`w-24 h-24 rounded-full ${indicatorColor}`} />}
        </div>

        {/* METİN ÇAKIŞMASI VE SIĞMAMA FİXİ: pr-64 eklendi, line-clamp-3'e çıkarıldı, font dengelendi */}
        <div className="flex-1 min-w-0 flex flex-col justify-center pr-64">
          <div className={`font-black leading-[1.1] tracking-tight break-words line-clamp-3 ${isCompact ? 'text-[7.5rem]' : 'text-[9.5rem]'} ${textColor}`} title={label}>{label}</div>

          {!isCompact && externalReqs && externalReqs.length > 0 && !completed && (
            <div className="mt-20 flex flex-wrap gap-12 text-[4rem] font-medium">
              {externalReqs.slice(0, 2).map((req, i) => (
                <div key={i} className="flex items-center gap-8 bg-[#050505] px-12 py-8 rounded-2xl border-[4px] border-red-900/30 text-red-400/80">
                  <AlertTriangle className="w-16 h-16 text-red-500/50 flex-shrink-0" />
                  <span className="truncate">Req: <span className="text-red-300 font-bold">{req.quest}</span></span>
                </div>
              ))}
              {externalReqs.length > 2 && <span className="text-5xl text-zinc-600 flex items-center px-8">+{externalReqs.length - 2}</span>}
            </div>
          )}
        </div>
      </div>

      <a
        href={wikiLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`absolute z-20 flex items-center justify-center transition-all duration-200 border-[4px] group/wiki ${isCompact
          ? 'bottom-16 right-16 w-48 h-48 rounded-2xl'
          : 'bottom-24 right-24 px-32 py-16 rounded-2xl gap-12 text-[5rem]'
          } ${completed
            ? 'bg-emerald-950/50 border-emerald-900/50 text-emerald-600 hover:bg-emerald-600 hover:text-white'
            : isActive
              ? 'bg-amber-950/30 border-amber-900/50 text-amber-600 hover:bg-amber-500 hover:text-white'
              : 'bg-[#050505] border-zinc-800 text-zinc-600 hover:border-zinc-600 hover:text-white'
          } font-bold shadow-xl hover:scale-105`}
      >
        {isCompact ? (
          <BookOpen size={80} strokeWidth={3} />
        ) : (
          <>
            <BookOpen className="w-20 h-20" strokeWidth={3} />
            <span>WIKI</span>
          </>
        )}
      </a>

      <Handle type="source" position={Position.Right} className="!bg-transparent !border-0 !w-32 !h-32 !-right-16" />
    </div>
  );
});